import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { IconBell, IconChevronRight } from "@tabler/icons-react";
import { cn } from "@/shared/lib/utils";
import { useNotificationCenter, ensureNotificationWsBridge, setNotificationNavigator, } from "../../model/store";
import { describeNotification, formatRelativeTime, isHiddenNotification, readNotificationCover, } from "../../model/describe";
import { hasNotificationTarget } from "../../model/resolveHref";
import styles from "./NotificationBell.module.scss";
export const NotificationBell = ({ language, floating = false, }) => {
    const ru = language === "ru";
    const navigate = useNavigate();
    const items = useNotificationCenter((s) => s.items);
    const unreadCount = useNotificationCenter((s) => s.unreadCount);
    const total = useNotificationCenter((s) => s.total);
    const isOpen = useNotificationCenter((s) => s.isOpen);
    const isLoading = useNotificationCenter((s) => s.isLoading);
    const isLoadingMore = useNotificationCenter((s) => s.isLoadingMore);
    const load = useNotificationCenter((s) => s.load);
    const loadMore = useNotificationCenter((s) => s.loadMore);
    const open = useNotificationCenter((s) => s.open);
    const close = useNotificationCenter((s) => s.close);
    const markAllRead = useNotificationCenter((s) => s.markAllRead);
    const openNotification = useNotificationCenter((s) => s.openNotification);
    // Скрываем generic-«пустышки» (system / неизвестный тип без текста) из
    // видимого списка — они не должны показываться как «Системное сообщение».
    const visibleItems = items.filter((n) => !isHiddenNotification(n));
    const hasMore = items.length < total;
    const btnRef = useRef(null);
    const panelRef = useRef(null);
    const [pos, setPos] = useState(null);
    // id уведомлений, чья обложка не загрузилась (404/протухшая ссылка) — рисуем
    // для них обычный тон-плейсхолдер вместо «битой» картинки.
    const [failedCovers, setFailedCovers] = useState(new Set());
    const markCoverFailed = (id) => setFailedCovers((prev) => {
        if (prev.has(id))
            return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
    });
    // Первичная загрузка + подписка на live-события (idempotent, один раз/сессию).
    useEffect(() => {
        ensureNotificationWsBridge();
        void load();
    }, [load]);
    // Регистрируем SPA-навигатор для стора: тост/центр живут вне контекста
    // роутера и переходят по уведомлению через этот navigate.
    useEffect(() => {
        setNotificationNavigator((to) => navigate(to));
        return () => setNotificationNavigator(null);
    }, [navigate]);
    const computePosition = useCallback(() => {
        const el = btnRef.current;
        if (!el)
            return;
        const rect = el.getBoundingClientRect();
        const panelWidth = 340;
        const margin = 8;
        // Прижимаем к правому краю кнопки, но не даём уехать за вьюпорт.
        let left = rect.right - panelWidth;
        left = Math.max(margin, Math.min(left, window.innerWidth - panelWidth - margin));
        setPos({ top: rect.bottom + margin, left });
    }, []);
    const handleToggle = () => {
        if (isOpen) {
            close();
        }
        else {
            computePosition();
            open();
        }
    };
    // Клик по элементу центра (или по стрелке «Перейти»): открыть цель уведомления
    // — хаб песни `/song/{token}?…` для медиа, либо прежний маршрут кабинета.
    // Пометка прочитанным + закрытие панели — внутри стора.
    const handleItemClick = (n) => {
        void openNotification(n);
    };
    // Закрытие по клику вне панели/кнопки, по Esc, и репозиция при resize/scroll.
    useEffect(() => {
        if (!isOpen)
            return;
        const onDown = (e) => {
            const target = e.target;
            if (panelRef.current?.contains(target) || btnRef.current?.contains(target)) {
                return;
            }
            close();
        };
        const onKey = (e) => {
            if (e.key === "Escape")
                close();
        };
        const onReflow = () => computePosition();
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        window.addEventListener("resize", onReflow);
        window.addEventListener("scroll", onReflow, true);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("resize", onReflow);
            window.removeEventListener("scroll", onReflow, true);
        };
    }, [isOpen, close, computePosition]);
    const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);
    const panel = isOpen && pos
        ? ReactDOM.createPortal(_jsxs("div", { ref: panelRef, className: styles.panel, style: { top: pos.top, left: pos.left }, role: "dialog", "aria-label": ru ? "Уведомления" : "Notifications", children: [_jsxs("div", { className: styles.panelHead, children: [_jsx("span", { className: styles.panelTitle, children: ru ? "Уведомления" : "Notifications" }), unreadCount > 0 && (_jsx("button", { type: "button", className: styles.readAll, onClick: () => void markAllRead(), children: ru ? "Прочитать всё" : "Mark all read" }))] }), _jsxs("div", { className: styles.list, children: [isLoading && visibleItems.length === 0 ? (_jsx("div", { className: styles.placeholder, children: ru ? "Загрузка…" : "Loading…" })) : visibleItems.length === 0 ? (_jsx("div", { className: styles.placeholder, children: ru ? "Пока нет уведомлений" : "No notifications yet" })) : (visibleItems.map((n) => {
                            const view = describeNotification(n, language);
                            const canGo = hasNotificationTarget(n);
                            // Обложка есть только у песенных уведомлений (payload.cover_url);
                            // у остальных (и у битой ссылки) — цветная точка-плейсхолдер.
                            const cover = failedCovers.has(n.id)
                                ? null
                                : readNotificationCover(n.payload);
                            return (_jsxs("div", { className: cn(styles.item, !n.is_read && styles.itemUnread), children: [_jsxs("button", { type: "button", className: styles.itemMain, onClick: () => handleItemClick(n), children: [cover ? (_jsxs("span", { className: styles.itemCover, "aria-hidden": "true", children: [_jsx("img", { className: styles.itemCoverImg, src: cover, alt: "", loading: "lazy", referrerPolicy: "no-referrer", onError: () => markCoverFailed(n.id) }), _jsx("span", { className: cn(styles.coverDot, styles[`dot_${view.tone}`]) })] })) : (_jsx("span", { className: cn(styles.dot, styles[`dot_${view.tone}`]), "aria-hidden": "true" })), _jsxs("span", { className: styles.itemBody, children: [_jsx("span", { className: styles.itemTitle, children: view.title }), _jsx("span", { className: styles.itemMessage, children: view.message }), _jsx("span", { className: styles.itemTime, children: formatRelativeTime(n.created_at, language) })] })] }), !n.is_read && (_jsx("span", { className: styles.unreadMark, "aria-label": ru ? "Не прочитано" : "Unread" })), canGo && (_jsx("button", { type: "button", className: styles.itemGo, onClick: () => handleItemClick(n), "aria-label": ru ? "Перейти" : "Open", children: _jsx(IconChevronRight, { size: 18, stroke: 1.8 }) }))] }, n.id));
                        })), hasMore && (_jsx("button", { type: "button", className: styles.loadMore, onClick: () => void loadMore(), disabled: isLoadingMore, children: isLoadingMore
                                ? ru
                                    ? "Загрузка…"
                                    : "Loading…"
                                : ru
                                    ? "Показать ещё"
                                    : "Show more" }))] })] }), document.body)
        : null;
    return (_jsxs(_Fragment, { children: [_jsxs("button", { ref: btnRef, type: "button", className: cn(styles.bell, floating && styles.bellFloating), onClick: handleToggle, "aria-label": ru ? "Уведомления" : "Notifications", "aria-expanded": isOpen, children: [_jsx(IconBell, { size: 20, stroke: 1.6 }), unreadCount > 0 && (_jsx("span", { className: styles.badge, "aria-hidden": "true", children: badgeLabel }))] }), panel] }));
};
